var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AnswerMessage, AskMessage, SUBJECT_TYPE_SEPERATOR, ByteStreamWriter, BulkMessage, UpdateFacetOnModelMessage, AskErrorMessage, wrapMessage, TYPE_HASH_BYTES_PER_ELEMENT, RemoveFacetFromModelMessage, isPromise, WorkerLocalMessage, CreateSubModelMessage } from "flavr-common";
import { SubscribeMessage, SystemInitMessage, GatherSystemStatsMessage, HandshakeModuleMessage } from "./BusMessages";
import { now } from "@messages/timing";
import { ENABLE_PROCESS_MESSAGE_STATS } from "./BusServiceBase";
const MSG_LENGTH_AND_TYPE_BYTE_LENGTH = Uint32Array.BYTES_PER_ELEMENT + TYPE_HASH_BYTES_PER_ELEMENT;
export class BusConnectionImpl {
    constructor(service, moduleId) {
        this.service = service;
        this.moduleId = moduleId;
        this.initReceived = false;
        this.waitForSystemInitMessage = (message, subjectSlashType, timeStamp, clientId, targetWorkerId, transport) => {
            if (this.isReady()) {
                this.sendInternalCallback = this.sendInternal;
                this.sendInternal(message, subjectSlashType, timeStamp, clientId, targetWorkerId, transport);
                return;
            }
            if (SystemInitMessage.is(message)) {
                if (this.isReady()) {
                    throw new Error(`[BusConnectionImpl] Connection ${this.moduleId} already initialized`);
                }
                this.sendInternalCallback = this.sendInternal;
                this.sendInternal(message, subjectSlashType, timeStamp, clientId, targetWorkerId, transport);
                return;
            }
            if (SubscribeMessage.is(message)) {
                this.sendInternal(message, subjectSlashType, timeStamp, clientId, targetWorkerId, transport);
                return;
            }
            if (HandshakeModuleMessage.is(message)) {
                this.sendToOtherWorkers(subjectSlashType, message, timeStamp, undefined);
                return;
            }
            if (AnswerMessage.is(message)) {
                this.sendInternal(message, subjectSlashType, timeStamp, undefined, undefined, transport);
                return;
            }
            if (this.pendingMessagesForSystemInit === undefined) {
                this.pendingMessagesForSystemInit = [];
            }
            this.pendingMessagesForSystemInit.push({
                message,
                subjectSlashType,
                timeStamp,
                clientId,
                targetWorkerId,
                transport
            });
            console.error(`[bus] Message ${message.type} can't be sent as long as the SystemInitMessage has not been received.`);
        };
        this.sendInternal = (message, subjectSlashType, timeStamp, clientId, targetWorkerId, transport) => {
            // If the answer is for this worker, process it immediately and don't send it to the bus.
            if (AnswerMessage.is(message) && message.targetWorkerId === this.service.getWorkerId()) {
                const callbackInfo = this.service.popAskData(message.askId);
                if (callbackInfo && !("dedicatedWorkerCallback" in callbackInfo)) {
                    callbackInfo.callback(message.messages, {
                        source: {
                            workerId: this.service.getWorkerId(),
                            moduleId: this.moduleId
                        },
                        timeStamp,
                        transport: transport !== null && transport !== void 0 ? transport : "normal"
                    });
                }
                else {
                    console.error(`[bus] No callback stored for askId ${message.askId}`);
                }
            }
            else {
                // Don't send local message to the worker
                if (message.subject !== "local") {
                    this.sendToOtherWorkers(subjectSlashType, message, timeStamp, clientId, targetWorkerId, transport);
                    // Ignore local if we have a target
                    if (targetWorkerId !== undefined)
                        return;
                }
                if (WorkerLocalMessage.is(message)) {
                    // message can be a bulk message, in this case we need to extract its messages which are themselves WorkerLocalMessages
                    const workerLocalMessages = message.isBulkMessage ? message.messages : [message];
                    let localMessages = workerLocalMessages
                        .reduceRight((prev, cur) => { prev.push(...cur.messages); return prev; }, []);
                    // Now the unpacked messages can be packed in bulk
                    if (localMessages.length > 0) {
                        localMessages = this.buildBulkMessageInfos(localMessages).map(info => info.bulkMessage);
                    }
                    for (const localMessage of localMessages) {
                        subjectSlashType = `${localMessage.subject}${SUBJECT_TYPE_SEPERATOR}${localMessage.type}`;
                        this.sendLocal(localMessage, timeStamp, subjectSlashType, clientId, "local");
                    }
                }
                else {
                    this.sendLocal(message, timeStamp, subjectSlashType, clientId, transport !== null && transport !== void 0 ? transport : "normal");
                }
            }
        };
        // Subscribe only so that busService can handle this message
        this.subscribe(GatherSystemStatsMessage.typestatic, (_msg, meta) => {
            this.service.reactToGatherMessage(meta.source.workerId === this.service.getWorkerId());
        });
        if (this.service.isReady()) {
            this.sendInternalCallback = this.sendInternal;
        }
        else {
            this.sendInternalCallback = this.waitForSystemInitMessage;
        }
    }
    onSystemInitReceived() {
        if (this.initReceived) {
            throw new Error(`Module ${this.moduleId} already initialized`);
        }
        this.initReceived = true;
        this.sendInternalCallback = this.sendInternal;
        if (this.pendingMessagesForSystemInit !== undefined) {
            for (const data of this.pendingMessagesForSystemInit) {
                this.sendInternal(data.message, data.subjectSlashType, data.timeStamp, data.clientId, data.targetWorkerId, data.transport);
            }
            delete this.pendingMessagesForSystemInit;
        }
    }
    isReady() {
        return this.service.isReady();
    }
    waitForConnectionReady() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isReady()) {
                yield new Promise((resolve, _reject) => {
                    const interval = setInterval(() => {
                        if (this.isReady()) {
                            clearInterval(interval);
                            resolve(true);
                        }
                    }, 1000);
                });
            }
        });
    }
    /**
     * subscribe to messages whose <subject>/<type> string matches the given pattern.
     * @param pattern a pattern to match against a messages string representation: <subject>/<type>
     * @param callback the method to call for Events matching the pattern
     * @return a handler for unsubscribing
     */
    subscribe(pattern, callback) {
        pattern = Array.isArray(pattern) ? pattern : [pattern];
        const handler = this.service.addSubscription(this.moduleId, pattern, callback);
        // if we already have a connection, inform the bus immediatly
        if (this.service.portConnected()) {
            this.service.postWrappedMessage(wrapMessage(new SubscribeMessage(pattern), this.service.getWorkerId(), "connect", Date.now()));
        }
        return handler;
    }
    /**
     * subscribe to message type
     * @param type the message type
     * @param callback the method to call for events matching the type
     * @param subPattern a pattern to match the message subtype
     * @return a handler for unsubscribing
     */
    subscribeByType(type, callback, subPattern) {
        const pattern = [`${type.typestatic}${subPattern !== null && subPattern !== void 0 ? subPattern : ""}`];
        const handler = this.service.addSubscription(this.moduleId, pattern, callback);
        // if we already have a connection, inform the bus immediately
        if (this.service.portConnected()) {
            this.service.postWrappedMessage(wrapMessage(new SubscribeMessage(pattern), this.service.getWorkerId(), "connect", Date.now()));
        }
        return handler;
    }
    subscribeByTypeOrAskRequest(type, subscriptionCallback, subPattern) {
        return this.subscribeByType(type, (msg, meta) => {
            if (AskMessage.is(msg)) {
                try {
                    const result = subscriptionCallback(msg.message, msg.askId, meta);
                    if (isPromise(result)) {
                        Promise.resolve(result).catch((error) => {
                            this.answer(msg.askId, new AskErrorMessage(JSON.stringify(error)));
                        });
                    }
                }
                catch (error) {
                    this.answer(msg.askId, new AskErrorMessage(JSON.stringify(error)));
                }
            }
            else {
                try {
                    const result = subscriptionCallback(msg, undefined, meta);
                    if (isPromise(result)) {
                        Promise.resolve(result).catch((error) => {
                            console.error('[bus]', error);
                        });
                    }
                }
                catch (error) {
                    console.error('[bus]', error);
                }
            }
        }, subPattern);
    }
    subscribeByFacetType(type, callback, subType) {
        let subPattern = type.typestatic;
        if (subType)
            subPattern += subType;
        return this.subscribeByType(UpdateFacetOnModelMessage, (msg, meta) => {
            callback(msg.uid, msg.facet, meta);
        }, subPattern);
    }
    subscribeByRemoveFacetType(type, callback, subType) {
        let subPattern = type.typestatic;
        // if (subType) subPattern += `@${subType}`;
        if (subType)
            subPattern += `${subType}`;
        return this.subscribeByType(RemoveFacetFromModelMessage, (msg, meta) => {
            callback(msg.uid, msg.facetType, meta);
        }, subPattern);
    }
    subscribeForAskRequest(type, askCallback, subPattern) {
        return this.subscribeByType(type, (msg, meta) => {
            if (!AskMessage.is(msg))
                return;
            try {
                const result = askCallback(msg.message, msg.askId, meta);
                if (isPromise(result)) {
                    Promise.resolve(result).catch((error) => {
                        this.answer(msg.askId, new AskErrorMessage(JSON.stringify(error)));
                    });
                }
            }
            catch (error) {
                this.answer(msg.askId, new AskErrorMessage(JSON.stringify(error)));
            }
        }, subPattern);
    }
    /**
     * unsubscribe a pattern with the handler returned from subscribe()
     */
    unsubscribe(handler) {
        const sub = this.service.removeSubscription(handler);
        if (sub !== undefined) {
            if (this.service.portConnected()) {
                // unsubscribe from bus
                this.service.postWrappedMessage(wrapMessage(new SubscribeMessage(sub.pattern, true), this.service.getWorkerId(), "connect", Date.now()));
            }
        }
        else {
            throw new Error(`Unsubscription invalid. no subscription for handler ${handler} found?!`);
        }
    }
    subscribeTransfer(pattern, callback) {
        this.service.addTransferSubscription(pattern, callback);
    }
    unsubscribeTransfer(pattern, callback) {
        this.service.removeTransferSubscription(pattern, callback);
    }
    /**
     * send a message to the bus and/or all other matching handlers or send multiple messages at once or answers to a ask
     * request
     * @param message
     * @param askId
     * @param timeStamp
     * @param clientId
     * @param targetWorkerId
     */
    send(message, askId, timeStamp, clientId, targetWorkerId, transport) {
        if (askId !== undefined) {
            this.answer(askId, message);
            return;
        }
        const subjectSlashType = `${message.subject}${SUBJECT_TYPE_SEPERATOR}${message.type}`;
        this.sendInternalCallback(message, subjectSlashType, timeStamp !== null && timeStamp !== void 0 ? timeStamp : Date.now(), clientId, targetWorkerId, transport);
    }
    sendToOtherWorkers(subjectSlashType, message, timeStamp, clientId, targetWorkerId, transport) {
        if (BulkMessage.is(message)) {
            this.sendBulkBuffer(message, timeStamp, subjectSlashType, clientId, targetWorkerId, transport);
            return;
        }
        else if (AskMessage.is(message)) {
            this.sendAskBuffer(message, timeStamp, subjectSlashType, targetWorkerId);
            return;
        }
        else if (AnswerMessage.is(message)) {
            this.sendAnswerBuffer(message, timeStamp, subjectSlashType);
            return;
        }
        this.sendBuffer(message, timeStamp, subjectSlashType, clientId, targetWorkerId, transport);
    }
    sendLocal(msg, timeStamp, pattern, clientId, transport = "internal") {
        const subscriptions = this.service.getSubscribersForMessageType(pattern);
        const meta = {
            source: {
                workerId: this.service.getWorkerId(),
                moduleId: this.moduleId
            },
            timeStamp,
            clientId,
            transport
        };
        // send the message to all local (same worker) peers
        for (const subscription of subscriptions) {
            if (this.moduleId === subscription.moduleId)
                continue;
            if (BulkMessage.is(msg)) {
                // use containing messages instead
                for (const m of msg.messages) {
                    this.forwardMessageToSubscription(m, subscription, meta);
                }
            }
            else {
                this.forwardMessageToSubscription(msg, subscription, meta);
            }
        }
    }
    sendBulkBuffer(bulkMsg, timeStamp, subjectSlashType, clientId, targetWorkerId, transport) {
        const firstMsg = bulkMsg.messages[0];
        if (firstMsg.typeHash === undefined)
            throw new Error("typeHash is not defined");
        let byteLength = MSG_LENGTH_AND_TYPE_BYTE_LENGTH;
        for (const msg of bulkMsg.messages) {
            byteLength += msg.byteLength || 0;
        }
        const buffer = new ArrayBuffer(byteLength);
        const stream = new ByteStreamWriter(buffer);
        stream.uint32(bulkMsg.messages.length);
        stream.typeHash(firstMsg.typeHash);
        for (const m of bulkMsg.messages) {
            try {
                m.serialization(stream);
            }
            catch (e) {
                console.error(`Error while serializing ${m.type}`, m, e);
            }
        }
        this.service.postBuffer(subjectSlashType, this.moduleId, timeStamp, buffer, undefined, targetWorkerId, clientId, transport);
    }
    sendAskBuffer(askMsg, timeStamp, subjectSlashType, targetWorkerId) {
        const msg = askMsg.message;
        if (msg.typeHash === undefined)
            throw new Error("typeHash is not defined");
        const byteLength = msg.byteLength + MSG_LENGTH_AND_TYPE_BYTE_LENGTH;
        const buffer = new ArrayBuffer(byteLength);
        const stream = new ByteStreamWriter(buffer);
        // message count
        stream.uint32(1);
        stream.typeHash(msg.typeHash);
        msg.serialization(stream);
        this.service.postBuffer(subjectSlashType, this.moduleId, timeStamp, buffer, askMsg.askId, targetWorkerId);
    }
    sendAnswerBuffer(answerMsg, timeStamp, subjectSlashType) {
        const firstMsg = answerMsg.messages[0];
        if (firstMsg === undefined)
            return;
        if (firstMsg.typeHash === undefined)
            throw new Error(`typeHash is not defined for msg ${firstMsg.type}`);
        let byteLength = Uint32Array.BYTES_PER_ELEMENT;
        for (const msg of answerMsg.messages) {
            byteLength += TYPE_HASH_BYTES_PER_ELEMENT + msg.byteLength;
        }
        const buffer = new ArrayBuffer(byteLength);
        const stream = new ByteStreamWriter(buffer);
        stream.uint32(answerMsg.messages.length);
        for (const msg of answerMsg.messages) {
            if (msg.typeHash === undefined)
                throw new Error("typeHash is not defined!");
            if (!msg.serialization)
                throw new Error("serialization is not defined!");
            stream.typeHash(msg.typeHash);
            msg.serialization(stream);
        }
        this.service.postBuffer(subjectSlashType, this.moduleId, timeStamp, buffer, answerMsg.askId, answerMsg.targetWorkerId);
    }
    sendBuffer(msg, timeStamp, subjectSlashType, clientId, targetWorkerId, transport) {
        if (msg.typeHash === undefined)
            throw new Error(`typeHash is not defined for msg ${msg.type}`);
        const byteLength = msg.byteLength + MSG_LENGTH_AND_TYPE_BYTE_LENGTH;
        const buffer = new ArrayBuffer(byteLength);
        const stream = new ByteStreamWriter(buffer);
        // message count
        stream.uint32(1);
        stream.typeHash(msg.typeHash);
        msg.serialization(stream);
        this.service.postBuffer(subjectSlashType, this.moduleId, timeStamp, buffer, undefined, targetWorkerId, clientId, transport);
    }
    forwardMessageToSubscription(message, subscription, metaData) {
        const time = ENABLE_PROCESS_MESSAGE_STATS ? now() : 0;
        subscription.callback(message, metaData);
        if (ENABLE_PROCESS_MESSAGE_STATS) {
            const elapsedTime = now() - time;
            this.service.processMessageStats(subscription.moduleId, AskMessage.is(message) ? message.message : message, metaData, elapsedTime);
        }
    }
    /**
     * send multiple messages at once or answers to a ask request
     * WARNING: The messages get sorted by type, so if you mix different message types together they
     * may not be processed in the order of the given array.
     * example:  given the pseudo input [ {type:'a',num:1}, {type:'b',num:2},{type:'a',num:3}
     *          will yield to a processing order: [ [{type:'a',num:1}, {type:'a',num:3}], [{type:'b',num:2}] ]
     */
    sendMultiple(messages, askId, timeStamp, clientId, targetWorkerId, transport) {
        if (askId !== undefined) {
            this.answer(askId, messages);
        }
        else {
            if (timeStamp === undefined)
                timeStamp = Date.now();
            const bulkMessageInfos = this.buildBulkMessageInfos(messages);
            for (const bulkMessageInfo of bulkMessageInfos) {
                this.send(bulkMessageInfo.bulkMessage, undefined, timeStamp, clientId, targetWorkerId, transport);
            }
        }
    }
    /**
     * send a ask request to one module
     */
    ask(message, callback) {
        this.send(this.service.createAskMessage(message, callback));
    }
    askByMessageType(message, type) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield new Promise((resolve, reject) => {
                this.ask(message, (answerMsgs) => {
                    if (answerMsgs.length !== 1) {
                        reject(answerMsgs);
                        return;
                    }
                    const msg = answerMsgs[0];
                    if (!type.is(msg)) {
                        reject(answerMsgs);
                        return;
                    }
                    resolve(msg);
                });
            });
        });
    }
    /**
     * send an answer to the ask request
     */
    answer(askId, message) {
        let targetWorkerId = this.service.popWorkerIdForAskId(askId);
        if (!targetWorkerId) {
            if (!this.service.hasAskData(askId)) {
                console.warn(`[bus] Can't handle answer for askId ${askId}`, message);
                return;
            }
            // Ask request comes from the same worker
            targetWorkerId = this.service.getWorkerId();
        }
        const messages = Array.isArray(message) ? message : [message];
        this.send(new AnswerMessage(askId, targetWorkerId, messages));
    }
    /**
     * handle message by the subscriptions of this connection
     */
    handleMessageYourself(message, metaData) {
        const subjectSlashType = `${message.subject}/${message.type}`;
        const subscribers = this.service
            .getSubscribersForMessageType(subjectSlashType)
            .filter(subscriber => subscriber.moduleId === this.moduleId);
        this.service.handleMessage(message, metaData, subscribers);
    }
    processMessagesFromServer(messages) {
        const timeStamp = Date.now();
        const bulkMessageInfos = this.buildBulkMessageInfos(messages);
        // we iterate over the map in insertion order and build bulkMessages from it
        for (const bulkMessageInfo of bulkMessageInfos) {
            this.sendToOtherWorkers(bulkMessageInfo.pattern, bulkMessageInfo.bulkMessage, timeStamp);
            // send to local modules if needed
            const subscriptions = this.service.getSubscribersForMessageType(bulkMessageInfo.pattern);
            if (!subscriptions)
                continue;
            const meta = {
                source: {
                    workerId: this.service.getWorkerId(),
                    moduleId: this.moduleId
                },
                timeStamp,
                transport: "normal"
            };
            this.service.handleMessage(bulkMessageInfo.bulkMessage.messages, meta, subscriptions);
        }
    }
    sendTransferableToWorker(id, targetWorkerId, pattern, objData, transfer) {
        this.service.transfer(id, targetWorkerId, pattern, objData, transfer);
    }
    /// Private helper methods ///
    buildBulkMessageInfos(messages) {
        // Batching messages can introduce edge cases like updating a model before its created, therefore we reorder the
        // messages to make sure the general order won't break
        const sortedMessages = this.sortByPriority(messages);
        // send multiple messages at once to the bus and/or all other matching handlers
        // groups the messages by their pattern signature and sends them together in BulkMessages
        const map = new Map();
        for (const msg of sortedMessages) {
            const collection = map.get(msg.type);
            if (collection === undefined) {
                map.set(msg.type, [msg]);
            }
            else {
                collection.push(msg);
            }
        }
        // we iterate over the map in insertion order and build bulkMessages from it
        const bulkMessages = [];
        for (const messages of map.values()) {
            const firstMsg = messages[0];
            const pattern = `${firstMsg.subject}${SUBJECT_TYPE_SEPERATOR}${firstMsg.type}`;
            // wrap them all in a bulk message and using the first message's pattern signature, as they are all equal
            bulkMessages.push({
                bulkMessage: new BulkMessage(messages, messages[0].subject, messages[0].type),
                pattern
            });
        }
        return bulkMessages;
    }
    sortByPriority(messages) {
        return messages
            .map((msg, index) => ({
            msg,
            index,
            priority: CreateSubModelMessage.is(msg) ? 0 : 1
        }))
            .sort((a, b) => {
            if (a.priority !== b.priority) {
                return a.priority - b.priority;
            }
            // preserve original order within same priority
            return a.index - b.index;
        })
            .map(entry => entry.msg);
    }
}
